import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";

import "./BudgetLineBulkCreate.css";

const BudgetLineBulkCreate = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const budget = location?.state?.budget;

    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState("");
    const [loading, setLoading] = useState(false);

    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "info"
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");

    const handleNotificationClose = () =>
        setNotification(prev => ({ ...prev, show: false }));

    const generateToken = () =>
        Math.random().toString(36).substring(2, 8).toUpperCase();

    if (!budget)
        return <p>No budget selected. Please navigate from the budget list.</p>;

    // -------------------------
    // Handle File Upload
    // -------------------------
    const handleFileUpload = (e) => {

        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();

        reader.onload = evt => {

            const data = new Uint8Array(evt.target.result);

            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];

            const worksheet = workbook.Sheets[sheetName];

            const json = XLSX.utils.sheet_to_json(worksheet);

            setRows(json);
        };

        reader.readAsArrayBuffer(file);
    };

    // -------------------------
    // Submit
    // -------------------------
    const handleSubmit = e => {
        e.preventDefault();

        if (rows.length === 0) {
            setNotification({
                show: true,
                message: "Please upload an Excel/CSV file first.",
                type: "error"
            });
            return;
        }

        const token = generateToken();

        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirm = async input => {

        if (input !== confirmToken) {
            setNotification({
                show: true,
                message: "Incorrect confirmation code.",
                type: "error"
            });
            setConfirmOpen(false);
            return;
        }

        setConfirmOpen(false);
        setLoading(true);

        try {

            const payload = {
                budgetId: budget._id,
                lines: rows
            };

            await privateApi.post("/api/budget-lines/bulk", payload);

            setNotification({
                show: true,
                message: "Budget lines uploaded successfully.",
                type: "success"
            });

            setRows([]);
            setFileName("");

        } catch (err) {

            setNotification({
                show: true,
                message:
                    err.response?.data?.message ||
                    "Failed to upload budget lines.",
                type: "error"
            });

        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => setConfirmOpen(false);

    return (
        <div className="budgetline-bulk-create">

            <h2>
                Bulk Upload Budget Lines (
                {budget?.lgaId
                    ? budget.lgaId.name
                    : budget?.stateId
                        ? budget.stateId.name
                        : "Federal"}
                )
            </h2>

            <Notification
                show={notification.show}
                message={notification.message}
                type={notification.type}
                onClose={handleNotificationClose}
            />

            <form onSubmit={handleSubmit}>

                <div className="bulk-upload-group">

                    <label>Upload Excel / CSV</label>

                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                    />

                    {fileName && (
                        <p className="file-name">
                            Uploaded: {fileName}
                        </p>
                    )}

                </div>

                {rows.length > 0 && (

                    <div className="preview-table">

                        <h3>Preview ({rows.length} rows)</h3>

                        <table>

                            <thead>
                                <tr>
                                    {Object.keys(rows[0]).map((key, i) => (
                                        <th key={i}>{key}</th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>

                                {rows.slice(0, 10).map((row, i) => (

                                    <tr key={i}>

                                        {Object.values(row).map((val, j) => (
                                            <td key={j}>{val}</td>
                                        ))}

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                        {rows.length > 10 && (
                            <p className="preview-note">
                                Showing first 10 rows only
                            </p>
                        )}

                    </div>

                )}

                <div className="bulk-actions">

                    <button
                        type="submit"
                        disabled={loading}
                        className="bulk-upload-btn"
                    >
                        {loading ? "Uploading..." : "Upload Budget Lines"}
                    </button>

                    <button
                        type="button"
                        className="bulk-back-btn"
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </button>

                </div>

            </form>

            <ConfirmDialog
                show={confirmOpen}
                token={confirmToken}
                action={`You are about to bulk upload ${rows.length} budget lines`}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

        </div>
    );
};

export default BudgetLineBulkCreate;